import { Controller } from "@hotwired/stimulus"
import { getItem, setItem } from "../utils/local_storage"

export default class extends Controller {
  static targets = ["body", "toc", "progress", "saveButton", "saveLabel", "status"]
  static values = { site: String, record: String }

  connect() {
    this.saved = getItem(this.key, false) === true
    this.syncSaved()
    this.buildToc()
    this.addCopyButtons()
    this.copyListener = event => this.copy(event)
    this.element.addEventListener("click", this.copyListener)
    this.progress()
  }

  disconnect() {
    this.element.removeEventListener("click", this.copyListener)
    if (this.frame) cancelAnimationFrame(this.frame)
  }

  get key() { return `devgab:signal:${this.siteValue}:article:${this.recordValue}` }

  toggleBookmark() {
    this.saved = !this.saved
    const persisted = setItem(this.key, this.saved)
    this.syncSaved()
    if (this.hasStatusTarget) this.statusTarget.textContent = persisted ? (this.saved ? "Saved to your reading list on this device." : "Removed from your reading list.") : "Updated for this visit. Your browser could not save it."
  }

  syncSaved() {
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.setAttribute("aria-pressed", String(this.saved))
      this.saveButtonTarget.classList.toggle("is-saved", this.saved)
    }
    if (this.hasSaveLabelTarget) this.saveLabelTarget.textContent = this.saved ? "Saved" : "Save for later"
  }

  buildToc() {
    if (!this.hasBodyTarget || !this.hasTocTarget) return
    const fragment = document.createDocumentFragment()
    this.bodyTarget.querySelectorAll("h2, h3").forEach((heading, index) => {
      if (!heading.id) {
        const base = heading.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || `section-${index + 1}`
        let id = base, suffix = 1
        while (document.getElementById(id)) id = `${base}-${++suffix}`
        heading.id = id
      }
      const link = document.createElement("a")
      link.href = `#${encodeURIComponent(heading.id)}`
      link.textContent = heading.textContent
      link.className = heading.tagName === "H3" ? "signal-toc-subheading" : ""
      fragment.append(link)
    })
    this.tocTarget.replaceChildren(fragment)
    this.tocTarget.closest("aside").hidden = !this.tocTarget.childElementCount
  }

  addCopyButtons() {
    if (!this.hasBodyTarget) return
    this.bodyTarget.querySelectorAll("pre").forEach(pre => {
      if (pre.querySelector("[data-signal-copy]") || pre.closest(".content-block--code")?.querySelector(".content-block-code__copy-btn")) return
      const button = document.createElement("button")
      button.type = "button"
      button.dataset.signalCopy = "true"
      button.className = "signal-copy-button"
      button.textContent = "Copy"
      button.setAttribute("aria-label", "Copy code")
      pre.append(button)
    })
  }

  async copy(event) {
    const button = event.target.closest("[data-signal-copy]")
    if (!button || !this.element.contains(button)) return
    const pre = button.closest("pre")
    const code = pre.querySelector("code")
    const copy = pre.cloneNode(true)
    copy.querySelectorAll("button").forEach(el => el.remove())
    const text = code ? code.textContent : copy.textContent
    try {
      await navigator.clipboard.writeText(text)
      button.textContent = "Copied"
    } catch {
      const range = document.createRange()
      range.selectNodeContents(code || pre)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
      button.textContent = "Selected — copy"
    }
  }

  progress() {
    if (!this.hasProgressTarget || !this.hasBodyTarget || this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      const rect = this.bodyTarget.getBoundingClientRect()
      const distance = Math.max(1, rect.height - window.innerHeight)
      const amount = Math.max(0, Math.min(1, -rect.top / distance))
      this.progressTarget.style.width = `${amount * 100}%`
    })
  }
}
